import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface AssessmentResult {
  id: string;
  sport: string;
  assessmentType: string;
  score: number;
  totalQuestions: number;
  percentage: number;
  timestamp: string;
  answers: Array<{
    question: string;
    userAnswer: string;
    correctAnswer: string;
    isCorrect: boolean;
  }>;
}

export interface UserProfile {
  userId: string;
  assessments: AssessmentResult[];
  skillLevels: {
    [sport: string]: {
      overall: 'beginner' | 'intermediate' | 'advanced';
      categories: {
        [category: string]: 'beginner' | 'intermediate' | 'advanced';
      };
    };
  };
}

export const useUserProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadUserProfile();
    } else {
      setProfile(null);
      setLoading(false);
    }
  }, [user]);

  const loadUserProfile = () => {
    try {
      const profileData = localStorage.getItem(`userProfile_${user?.id}`);
      if (profileData) {
        const parsedProfile = JSON.parse(profileData);
        setProfile(parsedProfile);
      } else {
        // Initialize empty profile
        const newProfile: UserProfile = {
          userId: user?.id || '',
          assessments: [],
          skillLevels: {}
        };
        setProfile(newProfile);
        saveUserProfile(newProfile);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const saveUserProfile = (profileData: UserProfile) => {
    try {
      localStorage.setItem(`userProfile_${user?.id}`, JSON.stringify(profileData));
      setProfile(profileData);
    } catch (error) {
      console.error('Error saving user profile:', error);
    }
  };

  const addAssessmentResult = (result: AssessmentResult) => {
    if (!profile) return;

    const updatedProfile = {
      ...profile,
      assessments: [...profile.assessments, result]
    };

    // Update skill levels based on assessment
    const sport = result.sport;
    if (!updatedProfile.skillLevels[sport]) {
      updatedProfile.skillLevels[sport] = {
        overall: 'beginner',
        categories: {}
      };
    }

    // Determine skill level based on percentage
    let skillLevel: 'beginner' | 'intermediate' | 'advanced' = 'beginner';
    if (result.percentage >= 80) {
      skillLevel = 'advanced';
    } else if (result.percentage >= 60) {
      skillLevel = 'intermediate';
    }

    // Update overall skill level (take the highest)
    const currentOverall = updatedProfile.skillLevels[sport].overall;
    if (skillLevel === 'advanced' || (skillLevel === 'intermediate' && currentOverall === 'beginner')) {
      updatedProfile.skillLevels[sport].overall = skillLevel;
    }

    // Update category skill level
    updatedProfile.skillLevels[sport].categories[result.assessmentType] = skillLevel;

    saveUserProfile(updatedProfile);
  };

  const getUserSkillLevel = (sport: string, category?: string): 'beginner' | 'intermediate' | 'advanced' => {
    if (!profile?.skillLevels[sport]) return 'beginner';
    
    if (category) {
      return profile.skillLevels[sport].categories[category] || 'beginner';
    }
    
    return profile.skillLevels[sport].overall;
  };

  const getWeakAreas = (sport: string): string[] => {
    if (!profile) return [];

    const sportAssessments = profile.assessments.filter(a => a.sport === sport);
    const weakAreas: string[] = [];

    sportAssessments.forEach(assessment => {
      if (assessment.percentage < 60) {
        weakAreas.push(assessment.assessmentType);
      }
    });

    return [...new Set(weakAreas)]; // Remove duplicates
  };

  const getRecentAssessments = (sport: string, limit: number = 5): AssessmentResult[] => {
    if (!profile) return [];

    return profile.assessments
      .filter(a => a.sport === sport)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, limit);
  };

  return {
    profile,
    loading,
    addAssessmentResult,
    getUserSkillLevel,
    getWeakAreas,
    getRecentAssessments,
    saveUserProfile
  };
};













